import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { Theme } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { useLocation } from "react-router";
import { NavLink, NavLinkProps } from "react-router-dom";
import { useObservable } from "src/framework/store/storeUtil";
import { ListItemButton, useMediaQuery, useTheme } from "@mui/material";
import { IMenuItem } from "src/framework/store/AppStore";
import { openMenu$ } from "src/framework/store/ApiEvents";
import theme from "src/theme/light";
import dayjs from "dayjs";
import MenuTag from "../MenuTag";

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
    },
    nested: {
      color: "#3E5055",
      paddingLeft: theme.spacing(1),
    },
    separator: {
      fontWeight: "bold",
      paddingLeft: theme.spacing(2),
    },
    expandable: {
      color: "rgba(0, 163, 53, 0.8)",
    },

    active: {
      color: "#fff",
      borderLeft: '6px solid #00AF39',
      background: "linear-gradient(90deg, #017226 0%, #1A1C1B 100%)",
    },
    subItem: {
      color: "#017226",
      background: "rgba(0, 114, 37, 0.2) !important",
    },

    icon: { color: "#fff" },
    iconDefault: { color: "#3E5055" },
    iconSubItem: { color: "#017226" },
  })
);

interface LinkBehaviorProps extends NavLinkProps {
  end?: boolean;
}


const MenuItem = ({
  menuItem,
  index = -1,
  current = -1,
  nested = false,
  setCurrent = () => null,
  linkList = [],
}: {
  menuItem: IMenuItem;
  nested: boolean;
  index?: number;
  current?: number;
  setCurrent?: Function;
  linkList?: any;
}) => {
  const classes = useStyles();

  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const openMenu = useObservable(openMenu$);
  const [lastVisitedUrl, setLastVisitedUrl] = useState('');
  const subItemSelected: boolean = menuItem?.menuItem?.find(i => i?.link === lastVisitedUrl) ? true : false;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const projetosFormSubMenuSelected = useMemo(() => {
    const fullUrl = location.pathname + location.search;
    let tipo = 'dimensionado';

    if (fullUrl.includes("personalizado")) {
      tipo = 'personalizado';
    }

    if (fullUrl.includes("vertical")) {
      tipo = 'vertical';
    }

    if (fullUrl.includes("dimensionado_offgrid")) {
      tipo = 'dimensionado_offgrid';
    }

    if (fullUrl.includes("dimensionado_hibrido")) {
      tipo = 'dimensionado_hibrido';
    }

    if (fullUrl.includes("personalizado_offgrid")) {
      tipo = 'personalizado_offgrid';
    }

    if (fullUrl.includes("personalizado_hibrido")) {
      tipo = 'personalizado_hibrido';
    }

    const status = !!(location.pathname.match(new RegExp("/projetos$")))
    
    return {
      status,
      fullUrl: `/projetos?tipo=${tipo}`
    }
  },[location.pathname, location.search])

  const menuSelected = useCallback(() : boolean => {
    if( menuItem.link === "/projetos/menu-projetos" && (location.pathname.match(new RegExp("clonar$")) || location.pathname.includes("/projetos/listagem/historico/") || location.pathname.includes("/dimensionados/edicao") || location.pathname.includes("/projetos/edicao_avancada/"))){
      return true;
    }

    if( menuItem.link === "/projetos" && location.pathname.includes("/projetos/kits/novo")){
      return true;
    }

    const projetosLinks = ['/projetos?tipo=dimensionado_hibrido', '/projetos?tipo=dimensionado', '/projetos?tipo=personalizado', '/projetos?tipo=vertical']
    
    if (projetosFormSubMenuSelected.status && menuItem.link && projetosLinks.includes(menuItem.link)) {
      return menuItem.link === projetosFormSubMenuSelected.fullUrl;
    }

    return location.pathname === menuItem.link || (!(linkList.includes(location.pathname)) && menuItem.link === lastVisitedUrl);
  },[lastVisitedUrl, linkList, location.pathname, menuItem.link, projetosFormSubMenuSelected.fullUrl, projetosFormSubMenuSelected.status])

  const isActive = menuItem.link ? menuSelected() : (open && subItemSelected);

  const isExpandable =
    menuItem.link === null &&
    !!menuItem.menuItem &&
    menuItem?.menuItem?.length > 0;

  const unitType = menuItem?.dataTag && String(menuItem?.dataTag)?.includes(':') ? undefined : 'day';
  const isNovo = menuItem?.diasTag && !dayjs().isAfter(dayjs(menuItem?.dataTag).add(menuItem?.diasTag, 'day'), unitType);

  const handleClose = () => {
    openMenu$.next(false);
  };
  
  useEffect(()=>{
    if(location.pathname){
      const fullUrl = projetosFormSubMenuSelected.status ? projetosFormSubMenuSelected.fullUrl : location.pathname;
      if(linkList.includes(fullUrl)){
        setLastVisitedUrl(fullUrl);
      }
    }
  },[location.pathname, linkList, location.search, projetosFormSubMenuSelected.status, projetosFormSubMenuSelected.fullUrl])

  useEffect(() => {
    if (isActive) {
      
    }
  }, [isActive, setCurrent, index, location.pathname]);
  
  useEffect(() => {
    if (!subItemSelected && open && current !== index) {
      setOpen(false);
    }
  }, [open, subItemSelected, current, index]);

  const handleClick = () => {
    if (menuItem.isSeparator) return;

    if ((isExpandable && !nested) || !isExpandable  || nested) {
      setCurrent(index);
    }

    if (isExpandable) {
      setOpen(!open);
    }

    if (isMobile && !isExpandable) {
      handleClose();
    }
  };

  const itemClass = isActive 
    ? (menuItem.isSubItem ? `${classes.subItem}` : `${classes.active}`) 
    : "";

  const controllClasses = nested 
    ? `${classes.nested} ` + itemClass 
    : itemClass

  const iconClasses = (open && subItemSelected) || menuSelected() 
    ? (menuItem.isSubItem ? `${classes.iconSubItem}` : `${classes.icon}`)
    : `${classes.iconDefault}`

  const LinkBehavior = forwardRef<HTMLAnchorElement, LinkBehaviorProps>(
    function LinkBehavior(props, ref) {
      return <NavLink ref={ref} {...props} />;
    }
  );

  return (
    <div>

      {menuItem.isExternalLink ? (
        <ListItem disablePadding className={controllClasses}>
          <ListItemButton
            color="primary"
            onClick={() => menuItem.link ? window.open(menuItem.link, menuItem?.targetLink) : {}}
          >
            {menuItem.icon && (
              <Tooltip title={menuItem?.text}>
                <ListItemIcon>
                  <Icon className={iconClasses}>{menuItem.icon}</Icon>
                </ListItemIcon>
              </Tooltip>
            )}
            {openMenu && (
              <>
                <ListItemText primary={menuItem.text} style={{ display: isExpandable ? 'block' : 'contents' }} />
                { isNovo && <MenuTag label="Novo" /> }
                {isExpandable ? open ? <ExpandLess /> : <ExpandMore /> : null}
              </>
            )}
          </ListItemButton>
        </ListItem>
      ) : (
        <>
          {menuItem.isSeparator ? (
            <ListItem
              color="primary"
              component={"span"}
              className={controllClasses}
              >
              {menuItem.icon && (
                <Tooltip title={menuItem?.text}>
                  <ListItemIcon>
                    <Icon className={iconClasses}>{menuItem.icon}</Icon>
                  </ListItemIcon>
                </Tooltip>
              )}
              {openMenu && (
                <ListItemText primary={menuItem.text} classes={{ primary: classes.separator }} />
              )}
            </ListItem>
          ) : (
            <ListItem disablePadding className={controllClasses}>
              <ListItemButton
                component={LinkBehavior}
                color="primary"
                to={menuItem.link || (location.pathname + location.search)}
                selected={menuSelected()}
                onClick={handleClick}
              >
                {menuItem.icon && (
                  <Tooltip title={menuItem?.text}>
                    <ListItemIcon>
                      <Icon className={iconClasses}>{menuItem.icon}</Icon>
                    </ListItemIcon>
                  </Tooltip>
                )}
                {openMenu && (
                  <>
                    <ListItemText primary={menuItem.text} style={{ display: isExpandable ? 'block' : 'contents' }}/>
                      { isNovo && <MenuTag label="Novo" /> }
                      {isExpandable ? open ? <ExpandLess /> : <ExpandMore /> : null}
                  </>
                )}
              </ListItemButton>
            </ListItem>
          )}
        </>
      )}
      <Collapse in={open} timeout="auto" unmountOnExit>
        {menuItem?.menuItem?.map((subMenu, index) => (
          <MenuItem
            menuItem={subMenu}
            nested={true}
            key={`subMenu-${btoa("subMenu-" + index)}`}
            linkList={linkList}
          />
        ))}
      </Collapse>
    </div>
  );
};

export { MenuItem };
